// src/services/userPreferences.js - MERGED VERSION WITH APICLIENT
import apiClient from '../lib/apiClient';

class UserPreferencesService {
  constructor() {
    // No need for apiBase since we're using apiClient
    this.cache = new Map();
    this.pendingRequests = new Map();
    this.offlineQueue = [];
    this.retryDelays = [1000, 2000, 4000];

    if (!window.__UPS_INIT_LOGGED__) {
      console.log('UserPreferencesService initialized with apiClient');
      window.__UPS_INIT_LOGGED__ = true;
    }
  }

  getAuthToken() {
    try {
      // First check for Supabase token in localStorage (IMPROVED)
      for (const key of Object.keys(localStorage)) {
        if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
          const raw = localStorage.getItem(key);
          if (!raw) continue;
          try {
            const parsed = JSON.parse(raw);
            if (parsed?.access_token) {
              console.log('✅ Found Supabase token');
              return parsed.access_token;
            }
          } catch (e) {
            console.warn('Failed to parse Supabase token:', e);
          }
        }
      }
      
      // Fallback to auth object
      const authObj = localStorage.getItem('auth');
      if (authObj) {
        try {
          const parsed = JSON.parse(authObj);
          const token = parsed.access_token || parsed.session?.access_token || null;
          if (token) {
            console.log('✅ Found auth token');
            return token;
          }
        } catch (e) {
          console.warn('Failed to parse auth object:', e);
        }
      }
      
      // Last resort
      const directToken = localStorage.getItem('access_token');
      if (directToken) {
        console.log('✅ Found direct access token');
        return directToken;
      }
      
      console.warn('⚠️ No auth token found');
      return null;
    } catch (e) {
      console.error('getAuthToken error:', e);
      return null;
    }
  }

  setAuthData(authData) {
    try {
      if (authData && (authData.access_token || authData.session?.access_token)) {
        localStorage.setItem('auth', JSON.stringify(authData));
        this.clearCache();
        return true;
      }
      return false;
    } catch (e) {
      console.error('setAuthData error:', e);
      return false;
    }
  }

  clearAuthToken() {
    try {
      localStorage.removeItem('auth');
      localStorage.removeItem('access_token');
      // Also clear Supabase tokens
      for (const key of Object.keys(localStorage)) {
        if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
          localStorage.removeItem(key);
        }
      }
    } catch (e) {
      console.error('clearAuthToken error:', e);
    }
  }

  // NEW: Wrapper around apiClient to maintain your error handling and queueing
  async makeApiRequest(path, options = {}) {
    try {
      let response;
      
      // Use apiClient methods based on the HTTP method
      if (options.method === 'PUT') {
        response = await apiClient.put(path, options.body);
      } else if (options.method === 'POST') {
        response = await apiClient.post(path, options.body);
      } else if (options.method === 'DELETE') {
        response = await apiClient.del(path);
      } else {
        // Default to GET
        response = await apiClient.get(path);
      }
      
      return response;
    } catch (error) {
      // Handle specific error types
      if (error.status === 401) {
        const authError = new Error('Authentication expired');
        authError.code = 'AUTH_EXPIRED';
        authError.status = 401;
        throw authError;
      }
      
      if (error.status >= 500) {
        const serverError = new Error(`Server error: ${error.status}`);
        serverError.code = 'SERVER_ERROR';
        serverError.status = error.status;
        throw serverError;
      }
      
      // Network errors
      if (error.name === 'TypeError' || /Failed to fetch/i.test(error.message)) {
        // Queue for offline retry
        this.queueOfflineRequest(path, options);
      }
      
      throw error;
    }
  }

  queueOfflineRequest(path, options) {
    this.offlineQueue.push({ path, options, timestamp: Date.now() });
    if (!this.onlineHandler) {
      this.onlineHandler = () => this.processOfflineQueue();
      window.addEventListener('online', this.onlineHandler);
    }
    console.log('📥 Queued request for offline retry:', path);
  }

  async processOfflineQueue() {
    if (this.offlineQueue.length === 0) return;
    console.log('🌐 Processing', this.offlineQueue.length, 'offline requests...');
    const q = [...this.offlineQueue];
    this.offlineQueue = [];
    
    for (const req of q) {
      try {
        await this.makeApiRequest(req.path, req.options);
        console.log('✅ Offline request succeeded:', req.path);
      } catch (e) {
        console.error('❌ Offline retry failed:', e);
        // Re-queue if still offline
        if (!navigator.onLine) {
          this.offlineQueue.push(req);
        }
      }
    }
  }

  async fetchAllPreferences() {
    const k = 'all-preferences';
    const hit = this.cache.get(k);
    
    // Use cached data if fresh (5 seconds)
    if (hit && Date.now() - hit.ts < 5000) {
      console.log('📦 Using cached preferences');
      return hit.data;
    }

    try {
      console.log('🔍 Fetching all preferences from backend...');
      
      // USE APICLIENT through our wrapper
      const data = await this.makeApiRequest('/api/user/preferences');
      
      // Validate response
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid preferences response from server');
      }
      
      console.log('✅ Fetched preferences:', data);
      this.cache.set(k, { ts: Date.now(), data });
      return data;
    } catch (e) {
      // If we have cached data and it's a network/server error, use cache
      if (hit && (e.code === 'SERVER_ERROR' || e.name === 'TypeError')) {
        console.warn('⚠️ Using stale cached preferences due to error:', e.message);
        return hit.data;
      }
      
      // For auth errors or no cache, throw the error
      console.error('❌ fetchAllPreferences failed:', e);
      throw e;
    }
  }

  async fetchPreference(type) {
    const k = `preference-${type}`;
    const hit = this.cache.get(k);
    
    if (hit && Date.now() - hit.ts < 5000) {
      return hit.data;
    }

    try {
      // USE APICLIENT through our wrapper
      const data = await this.makeApiRequest(`/api/user/preferences/${type}`);
      this.cache.set(k, { ts: Date.now(), data });
      return data;
    } catch (e) {
      // Use cache for network errors, throw for auth errors
      if (hit && (e.code === 'SERVER_ERROR' || e.name === 'TypeError')) {
        console.warn(`Using cached ${type} due to error:`, e.message);
        return hit.data;
      }
      console.error(`fetchPreference ${type} failed:`, e);
      throw e;
    }
  }

  async updatePreference(type, value) {
    console.log(`💾 Updating ${type}:`, value);

    // CRITICAL: Validate value before sending
    if (value === undefined) {
      console.error(`❌ Cannot update ${type}: value is undefined`);
      throw new Error('Value is undefined');
    }

    if (value === null) {
      console.error(`❌ Cannot update ${type}: value is null`);
      throw new Error('Value is null');
    }

    console.log('  Value type:', typeof value);
    console.log('  Value stringified:', JSON.stringify(value));

    // Clear related caches
    this.cache.delete('all-preferences');
    
    // Optimistic update
    this.cache.set(`preference-${type}`, { 
      ts: Date.now(), 
      data: value, 
      optimistic: true 
    });

    try {
      const bodyToSend = { value: value };
      console.log('📤 Body to send:', bodyToSend);
      console.log('📤 Stringified body:', JSON.stringify(bodyToSend));
      
      // USE APICLIENT through our wrapper - CRITICAL FIX
      // apiClient will handle JSON.stringify internally
      const res = await this.makeApiRequest(`/api/user/preferences/${type}`, {
        method: 'PUT',
        body: bodyToSend  // Pass the object, not stringified
      });
      
      console.log('✅ Preference updated successfully:', res);
      
      const finalVal = res?.preference_value ?? value;
      this.cache.set(`preference-${type}`, { 
        ts: Date.now(), 
        data: finalVal, 
        optimistic: false 
      });
      return res;
    } catch (e) {
      console.error(`❌ Failed to update ${type}:`, e.message);
      console.error('Full error:', e);
      
      // Remove optimistic update on failure
      this.cache.delete(`preference-${type}`);
      
      // Provide better error message
      if (e.message.includes('Missing value')) {
        throw new Error(`Backend rejected update for ${type}: Missing value`);
      }
      throw e;
    }
  }

  async deletePreference(type) {
    console.log(`🗑️ Deleting preference: ${type}`);
    
    // Clear caches
    this.cache.delete('all-preferences');
    this.cache.delete(`preference-${type}`);
    
    try {
      // USE APICLIENT through our wrapper
      const response = await this.makeApiRequest(`/api/user/preferences/${type}`, { 
        method: 'DELETE' 
      });
      console.log('✅ Preference deleted successfully');
      return response;
    } catch (error) {
      console.error(`❌ Failed to delete ${type}:`, error);
      throw error;
    }
  }

  async batchUpdatePreferences(updates) {
    console.log('📦 Batch updating preferences:', Object.keys(updates));
    
    this.cache.clear();
    
    const results = await Promise.all(
      Object.entries(updates).map(async ([type, value]) => {
        try {
          await this.updatePreference(type, value);
          return { type, ok: true };
        } catch (e) {
          return { type, error: e.message };
        }
      })
    );
    
    return {
      successful: results.filter(r => r.ok),
      failed: results.filter(r => r.error)
    };
  }

  clearCache() {
    console.log('🧹 Clearing preferences cache');
    this.cache.clear();
  }

  cleanup() {
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
    }
    this.cache.clear();
    this.pendingRequests.clear();
    this.offlineQueue = [];
  }
}

const userPreferencesService = new UserPreferencesService();
export default userPreferencesService;
export { UserPreferencesService };